import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';
import { BehaviorSubject, forkJoin, Observable, of, ReplaySubject, switchMap, throwError } from 'rxjs';
import { catchError, filter, map, startWith, tap } from 'rxjs/operators';
import {
    CustomFormControlAccessWithLoaders
} from 'src/app/_core/tools/custom-form-control-access';
import {
    ComplementarySubventionInfo
} from 'src/app/_shared/dialogs/shared-advanced-rules/shared-advanced-rule-edit-dialog/shared-advanced-rule-edit-dialog.component';
import {
    AdvancedRule,
    AdvancedRuleContent,
    AdvancedRuleService
} from 'src/app/_shared/services/advanced-rule/advanced-rule.service';
import { TypeActivite } from 'src/app/board/activity/activity.constants';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import {
    ModeleEnveloppeDto,
    MoteurRegleModelDto,
    RegleAvanceeModelDto,
    TypeArrondiModel,
    TypesRegleEnum
} from 'src/graphql/generated-types';
import { AllocationsContext } from 'src/app/_shared/services/allocations/detail/allocation.service';

const RULE_LABEL_MAX_LENGTH = 255;

const SubventionRoundingType = 'S';
const PriceRoundingType = 'P';

@Component({
    selector: 'app-shared-advanced-rule',
    templateUrl: './shared-advanced-rule.component.html',
    styleUrls: ['./shared-advanced-rule.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAdvancedRuleComponent),
    standalone: false
})
export class SharedAdvancedRuleComponent extends CustomFormControlAccessWithLoaders implements OnInit {

    private ruleType$: BehaviorSubject<TypesRegleEnum | null> = new BehaviorSubject<TypesRegleEnum>(null);
    private ruleId$: BehaviorSubject<number | null> = new BehaviorSubject<number>(null);

    private readonly advancedRuleSubject = new ReplaySubject<AdvancedRule | RegleAvanceeModelDto | null>(1);
    private readonly advancedRule$ = this.advancedRuleSubject.asObservable();

    @Input() activityType: TypeActivite | null = null;
    @Input() allocationType: string | null = null;
    @Input() @Coerce('boolean') isInterActivity: boolean;
    @Input() @Coerce('boolean') isPlanning: boolean;
    @Input() @Coerce('boolean') isDuplicate: boolean;
    @Input() @Coerce('boolean') hideInteractivityRulePicker: boolean;
    @Input() set ruleType(ruleType: TypesRegleEnum) {
        this.ruleType$.next(ruleType);
    }
    @Input() set ruleId(ruleId: number) {
        this.ruleId$.next(ruleId);
    }
    @Input() complementarySubvention: ComplementarySubventionInfo;

    readonly roundingI18nSelect = {
        ['+']: 'Arrondi supérieur (plafond)',
        ['-']: 'Arrondi inférieur (plancher)',
        ['=']: 'Arrondi au plus proche'
    };

    readonly subventionLabeli18nSelect = {EPA: 'Abondement', other: 'Subvention'};

    readonly ruleTypeEnum = TypesRegleEnum;

    form: FormGroup;

    roundingTypes: Record<string, { precision: number[], roundingType: string[] }> = {
        null: {precision: [], roundingType: []}
    };

    interActivityRuleList: MoteurRegleModelDto[] = [];

    complementaryEnvelopeList: ModeleEnveloppeDto[] = [];

    private roundingTypeList: TypeArrondiModel[] = [];

    private isAdvancedRuleDisabled = false;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly advancedRuleService: AdvancedRuleService,
                private readonly fb: FormBuilder) {
        super(cdr);
    }

    get selectedRoundingType(): string | undefined {
        return this.form.getRawValue()?.TypeSP;
    }

    get isComplementaryEnvelopSubvention(): boolean {
        return !!this.form.value?.IsSubventionEnveloppeComplementaire;
    }

    get shouldDisplayInterActivityRulePicker(): boolean {
        return !this.isInterActivity
            && !this.hideInteractivityRulePicker
            && !this.ruleId
            && ![
                TypeActivite.Mediatheque,
                TypeActivite.RessourcesPartagees,
                TypeActivite.Epargnes,
                TypeActivite.Pret
            ].includes(this.activityType)
            && arrayNotEmpty(this.interActivityRuleList);
    }

    get isSubventionRule(): boolean {
        return this.selectedRuleType === TypesRegleEnum.Subvention;
    }

    get isMediaOrResourcesActivity(): boolean {
        return [TypeActivite.Mediatheque, TypeActivite.RessourcesPartagees].includes(this.activityType);
    }

    get isMediaOrBenefitsActivity(): boolean {
        return [TypeActivite.Mediatheque, TypeActivite.Prestation].includes(this.activityType);
    }

    get isMedia(): boolean {
        return TypeActivite.Mediatheque === this.activityType;
    }

    get hasId(): boolean {
        return !!this.form.value.Id;
    }

    get shouldShowSubventionTypeSelect(): boolean {
        return !this.isInterActivity
            && this.activityType !== TypeActivite.Epargnes
            && !this.isPlanning;
    }

    get selectedRuleType(): TypesRegleEnum {
        return this.form?.value?.TypeRegle || this.ruleType;
    }

    get ruleType(): TypesRegleEnum {
        return this.ruleType$.value;
    }

    get ruleId(): number {
        return this.ruleId$.value;
    }

    get isAllocationContext(): boolean {
        return [
            AllocationsContext.ATB,
            AllocationsContext.PMV,
            AllocationsContext.ART
        ].some(context => context === this.allocationType);
    }

    validate(c: AbstractControl): Observable<{advancedrule: boolean} | null> {
        return of(this.form.valid ? null : {advancedrule: true});
    }

    writeValue(value: AdvancedRule | RegleAvanceeModelDto | null): void {
        super.writeValue(value);
        this.advancedRuleSubject.next(value);
    }

    setDisabledState(isDisabled: boolean): void {
        this.isAdvancedRuleDisabled = isDisabled;
    }

    private updateForm(advancedRule: AdvancedRuleContent): void {
        const rule: AdvancedRule = advancedRule?.Regle || null;
        const isDuplicate = !!this.isDuplicate;

        const {
            Valeur,
            TypeArrondi,
            TypeSP
        } = this.roundingTypeList.find(({IdTypeArrondi}) => IdTypeArrondi === advancedRule?.Regle?.IdTypeArrondi) || {};

        this.form.patchValue({
            IdRegleInterActivite: null,
            Id: isDuplicate ? 0 : (rule?.Id || null),
            IdOperateur: rule?.IdOperateur || null,
            IdRegleApplication: rule?.IdRegleApplication || null,
            IdRegleNonApplication: rule?.IdRegleNonApplication || null,
            IdTypeArrondi: rule?.IdTypeArrondi || null,
            IdTypeDate: rule?.IdTypeDate || null,
            IsComplementaire: rule?.IsComplementaire || false,
            IsForcageAutorise: rule?.IsForcageAutorise || false,
            IsGroupe: rule?.IsGroupe || false,
            IsSubChoisie: rule?.IsSubChoisie || false,
            IsTransverse: rule?.IsTransverse || this.isInterActivity || false,
            IsVenteSansSubAutorise: rule?.IsVenteSansSubAutorise || this.isMediaOrBenefitsActivity,
            Jours: rule?.Jours || null,
            Libelle: (rule?.Libelle || '') + (isDuplicate ? ' - copie' : ''),
            Mois: rule?.Mois || null,
            Subvention: rule?.Subvention || 0,
            SubventionDefaut: rule?.SubventionDefaut || 0,
            TypeRegle: rule?.TypeRegle || this.ruleType,
            ReglesEnfants: rule?.ReglesEnfants || [({})], // une ligne par défaut
            TypeSP: TypeSP || null,
            Precision: Valeur || null,
            TypeArrondi: TypeArrondi || null,
            IsSubventionEnveloppeComplementaire: rule?.IsComplementaire || false,
            IdEnveloppeComplementaire: isDuplicate ? null : (rule?.IdEnveloppe || null),
            Ordre: rule?.Ordre || null
        }, {emitEvent: false});

        if ((!this.isInterActivity && !!rule?.IsTransverse && !!rule.Id) || this.isAdvancedRuleDisabled) {
            this.toggleFormFields({enable: false, excludedFormControlName: []});
            this.form.get('Id').enable({emitEvent: false});
            this.form.get('IsTransverse').enable({emitEvent: false});
        } else {
            this.toggleFormFields({enable: true, excludedFormControlName: []});
            this.form.get('IsComplementaire')[this.hasId ? 'disable' : 'enable']({emitEvent: false});
            this.toggleRoundingFields({enable: !!TypeSP});
            this.toggleComplementarySubventionFields({enable: this.isComplementaryEnvelopSubvention && this.shouldShowSubventionTypeSelect});
        }

        this.cdr.markForCheck();
    }

    private createRoundingLists(): void {
        const subventionTypesList = this.roundingTypeList.filter(({TypeSP}) => TypeSP === SubventionRoundingType);
        const priceTypesList = this.roundingTypeList.filter(({TypeSP}) => TypeSP === PriceRoundingType);

        [
            {key: 'S', list: subventionTypesList},
            {key: 'P', list: priceTypesList}
        ].forEach(({
                       key,
                       list
                   }) => this.roundingTypes[key] = {
            roundingType: [...new Set(list.map(({TypeArrondi}) => TypeArrondi))],
            precision: [...new Set(list.map(({Valeur}) => Valeur))]
        });
    }

    private initSubscriptions(): void {
        const {
            TypeSP: typeSpControl,
            IsSubventionEnveloppeComplementaire: isSubventionEnveloppeComplementaryControl,
            Id: idControl,
            IsTransverse: isTransverseControl
        } = this.form.controls;

        this.watch$ = typeSpControl.valueChanges.pipe(
            tap(type => this.toggleRoundingFields({enable: !!type}))
        );

        this.watch$ = isSubventionEnveloppeComplementaryControl.valueChanges.pipe(
            tap(isComplementarySub => this.toggleComplementarySubventionFields({enable: !!isComplementarySub}))
        );

        if (this.shouldDisplayInterActivityRulePicker) {
            this.watch$ = isTransverseControl.valueChanges.pipe(
                tap(isInterActivity => {
                    this.toggleFormFields({
                        enable: !isInterActivity,
                        excludedFormControlName: ['idControl', 'IsTransverse']
                    });
                    idControl[isInterActivity ? 'enable' : 'disable']({emitEvent: false});
                })
            );

            this.watch$ = idControl.valueChanges.pipe(
                tap(() => this.loading = true),
                switchMap(id => this.advancedRuleService.getAdvancedRule$(id, this.selectedRuleType)),
                tap(rule => {
                    this.loading = false;
                    this.updateForm(rule);
                }),
                catchError(err => {
                    this.loading = false;
                    return throwError(err);
                })
            );
        }

        this.watch$ = this.form.valueChanges.pipe(
            startWith(this.form.getRawValue()),
            tap(values => {
                this.notifyUpdate(values);
                this.form.updateValueAndValidity({emitEvent: false});
            })
        );
    }

    private toggleFormFields({enable, excludedFormControlName} = {enable: false, excludedFormControlName: []}): void {
        Object.keys(this.form.controls).forEach(key => {
            if (!excludedFormControlName.includes(key)) {
                this.form.get(key)[enable ? 'enable' : 'disable']({emitEvent: false});
            }
        });
    }

    private toggleRoundingFields({enable} = {enable: false}): void {
        [
            'Precision',
            'TypeArrondi'
        ].forEach(k => this.form.get(k)[enable ? 'enable' : 'disable']({emitEvent: false}));
    }

    private toggleComplementarySubventionFields({enable} = {enable: false}): void {
        [
            'IdEnveloppeComplementaire',
            'Ordre'
        ].forEach(k => this.form.get(k)[enable ? 'enable' : 'disable']({emitEvent: false}));
    }

    private parseRule$(rule: AdvancedRule | RegleAvanceeModelDto | null): Observable<AdvancedRuleContent> {
        const action$ = !!rule
            ? of(this.advancedRuleService.flattenChildrenRules({
                Regle: {
                    ...rule,
                    ReglesEnfants: {
                        RegleAvanceeModelDTO: rule.ReglesEnfants
                            ? Array.isArray(rule.ReglesEnfants)
                                ? rule.ReglesEnfants
                                : rule.ReglesEnfants.RegleAvanceeModelDTO
                            : undefined
                    }
                }
            }))
            : this.wrapLoader$(this.advancedRuleService.getAdvancedRule$(this.ruleId, this.selectedRuleType));

        return action$.pipe(
            switchMap(rule => rule.Regle.IsComplementaire
                ? this.advancedRuleService.getComplementaryRuleEnvelopeId$(rule.Regle.Id).pipe(
                    map(enveloppeId => {
                        const {Ordre, IdEnveloppeComplementaire} = this.complementarySubvention || {};
                        return {
                            ...rule,
                            Regle: {
                                ...rule.Regle,
                                IdEnveloppe: IdEnveloppeComplementaire || enveloppeId,
                                IsComplementaire: true,
                                Ordre
                            }
                        };
                    })
                )
                : of(rule)
            ),
            tap(advancedRule => {
                this.updateForm(advancedRule);
                this.form.updateValueAndValidity({emitEvent: false});
                this.cdr.markForCheck();
            })
        );
    }

    private getRuleTypeControls(): ValidatorFn[] {
        if ([
            TypesRegleEnum.QuotaFaciale,
            TypesRegleEnum.QuotaNombre,
            TypesRegleEnum.Quota,
        ].includes(this.selectedRuleType)) {
            return [
                c => [
                    TypesRegleEnum.QuotaFaciale,
                    TypesRegleEnum.QuotaNombre
                ].includes(c.value) ? null : {specifyquotatype: true}
            ];
        }

        return [];
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            CalculSubvention: [null],
            CalculSubventionDefaut: [null],
            Id: [null],
            IdOperateur: [null],
            IdRegleApplication: [null],
            IdRegleNonApplication: [null],
            IdTypeDate: [null],
            IsComplementaire: [false],
            IsForcageAutorise: [false],
            IsGroupe: [false],
            IsSubChoisie: [false],
            IsTransverse: [false],
            IsVenteSansSubAutorise: [null],
            Jours: [null],
            Libelle: [null, [FormValidator.isNotNull, Validators.maxLength(RULE_LABEL_MAX_LENGTH)]],
            Mois: [null],
            Molecule: [null],
            RegleParent: this.fb.group({
                Id: [null],
            }),
            ReglesEnfants: [({})], // Une ligne par défaut
            Subvention: [0],
            SubventionDefaut: [0],
            TypeRegle: [null, this.getRuleTypeControls()],

            TypeSP: [null],
            Precision: [null, [FormValidator.isNotNull]],
            TypeArrondi: [null, [FormValidator.isNotNull]],

            IsSubventionEnveloppeComplementaire: [false],
            IdEnveloppeComplementaire: [{value: null, disabled: false}, [FormValidator.isNotNull]],
            Ordre: [{value: null, disabled: false}, [FormValidator.isNotNull, FormValidator.int, Validators.min(1)]],
        });

        this.watch$ = this.wrapLoader$(forkJoin([
            this.advancedRuleService.loadRelatedLists$(this.selectedRuleType),
            !this.isInterActivity ? this.advancedRuleService.getAdvancedInteractivityRuleListByRuleType$(this.selectedRuleType) : of([]),
            this.selectedRuleType === TypesRegleEnum.Subvention && !this.isInterActivity ? this.advancedRuleService.getComplementaryEnvelopeList() : of([]),
            this.isSubventionRule ? this.advancedRuleService.getRoundingTypesList$() : of([])
        ])).pipe(
            tap(([, interactivityRules, complementaryEnvelopeList, roundingTypesList]) => {
                this.roundingTypeList = roundingTypesList;
                this.interActivityRuleList = interactivityRules;
                this.complementaryEnvelopeList = complementaryEnvelopeList;
                this.createRoundingLists();
            }),
            tap(() => this.initSubscriptions()),
            switchMap(() => this.advancedRule$),
            switchMap((value) => this.parseRule$(value)),
        );
    }

}
